import type { SparkleLinkProps } from "@dust-tt/sparkle";
import { forwardRef } from "react";
import { Link } from "react-router-dom";

// Browser-native replacement for node `url.format` on a Next-style UrlObject
// ({ pathname, query, hash, search }). Keeps node's `url` module out of the SPA
// client bundle — otherwise it resolves to an externalized stub and throws
// "url.format is not a function" the moment a link with an object href renders.
function formatHref(href: unknown): string {
  const u = (href ?? {}) as {
    pathname?: string | null;
    search?: string | null;
    query?:
      | string
      | Record<string, string | number | boolean | null | undefined>
      | null;
    hash?: string | null;
  };
  const pathname = u.pathname ?? "";
  let search = "";
  if (typeof u.search === "string" && u.search) {
    search = u.search.startsWith("?") ? u.search : `?${u.search}`;
  } else if (u.query) {
    if (typeof u.query === "string") {
      const q = u.query.replace(/^\?/, "");
      search = q ? `?${q}` : "";
    } else {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(u.query)) {
        if (value !== undefined && value !== null) {
          params.set(key, String(value));
        }
      }
      const qs = params.toString();
      search = qs ? `?${qs}` : "";
    }
  }
  const hash = u.hash ? (u.hash.startsWith("#") ? u.hash : `#${u.hash}`) : "";
  return `${pathname}${search}${hash}`;
}

// Link wrapper that uses React Router's Link for SPA navigation
export const ReactRouterLinkWrapper = forwardRef<
  HTMLAnchorElement,
  SparkleLinkProps
>(function ReactRouterLinkWrapper(
  { href, children, shallow: _shallow, replace, prefetch: _prefetch, ...props },
  ref
) {
  // Convert UrlObject to string if needed
  const hrefString = typeof href !== "string" ? formatHref(href) : href;

  // For external links or API routes, use regular anchor
  if (
    hrefString.startsWith("http://") ||
    hrefString.startsWith("https://") ||
    hrefString.startsWith("/api/")
  ) {
    return (
      <a ref={ref} href={hrefString} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link ref={ref} to={hrefString} replace={replace} {...props}>
      {children}
    </Link>
  );
});
